import { SLOT_COLORS } from '@/components/truereturn/TrueReturnPicker';
import NewsWatchStar from '@/components/NewsWatchStar';

function Pct({ v, testId }) {
  if (v == null) return <span className="text-muted-foreground" data-testid={testId}>—</span>;
  const cls = v > 0 ? 'text-emerald-600 dark:text-emerald-400' : v < 0 ? 'text-rose-600 dark:text-rose-400' : '';
  return <span className={`font-mono tabular-nums ${cls}`} data-testid={testId}>{v > 0 ? '+' : ''}{v.toFixed(2)}%</span>;
}

const fmtUsd = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export default function TrueReturnStats({ tickers, series, dripOn, asOf, onOpenStock }) {
  const rows = tickers.map((t, i) => ({ t, i, s: series?.[t] })).filter((r) => r.s && !r.s.error);
  if (!rows.length) return null;
  return (
    <div className="overflow-x-auto rounded-xl border" data-testid="true-return-stats">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wide text-muted-foreground border-b bg-muted/30">
            <th className="text-left px-3 py-2">Ticker</th>
            <th className="text-right px-3 py-2">YTD</th>
            <th className="text-right px-3 py-2">YoY</th>
            <th className="text-right px-3 py-2">Fee</th>
            <th className="text-right px-3 py-2">Start Price</th>
            <th className="text-right px-3 py-2">End Price</th>
            <th className="text-right px-3 py-2">Overall True Return</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ t, i, s }) => {
            const mode = dripOn[t] === false ? 'flat' : 'drip';
            const st = s.stats[mode];
            return (
              <tr key={t} className="border-b last:border-b-0" data-testid={`true-return-stats-row-${t}`}>
                <td className="px-3 py-2 min-w-[180px] max-w-[260px]">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-sm shrink-0" style={{ background: SLOT_COLORS[i] }} />
                    <NewsWatchStar ticker={t} isDividend={s.is_dividend !== false} variant="table" />
                    <button
                      type="button"
                      onClick={() => onOpenStock?.(t)}
                      className="font-mono font-bold text-base hover:underline underline-offset-2"
                      style={{ color: SLOT_COLORS[i] }}
                      title="Open full stock data"
                      data-testid={`true-return-stats-ticker-${t}`}
                    >
                      {t}
                    </button>
                    {mode === 'drip' && <span className="text-[10px] text-muted-foreground">DRIP</span>}
                  </div>
                  {s.name && <p className="mt-1 truncate text-xs text-muted-foreground" title={s.name} data-testid={`true-return-stats-name-${t}`}>{s.name}</p>}
                  {s.starts_at_inception && <p className="text-[10px] text-muted-foreground mt-0.5">$10K starts at first trading day {s.start_date}</p>}
                </td>
                <td className="text-right px-3 py-2"><Pct v={st.ytd_pct} testId={`true-return-ytd-${t}`} /></td>
                <td className="text-right px-3 py-2"><Pct v={st.yoy_pct} testId={`true-return-yoy-${t}`} /></td>
                <td
                  className={`text-right px-3 py-2 font-mono tabular-nums text-xs ${
                    s.expense_ratio != null && Number(s.expense_ratio) >= 1
                      ? 'text-rose-600 dark:text-rose-400 font-semibold'
                      : ''
                  }`}
                  data-testid={`true-return-fee-${t}`}
                >
                  {s.expense_ratio == null ? <span className="text-muted-foreground">—</span> : `${Number(s.expense_ratio).toFixed(2)}%`}
                </td>
                <td className="text-right px-3 py-2 font-mono tabular-nums">
                  {fmtUsd(st.start_price)}
                  <div className="text-[10px] text-muted-foreground">{s.start_date}</div>
                </td>
                <td className="text-right px-3 py-2 font-mono tabular-nums">
                  {fmtUsd(st.end_price)}
                  <div className="text-[10px] text-muted-foreground">{s.end_date}</div>
                </td>
                <td className="text-right px-3 py-2">
                  <Pct v={st.overall_pct} testId={`true-return-overall-${t}`} />
                  <div className="text-[10px] text-muted-foreground font-mono">{fmtUsd(st.end_value)} today</div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="px-3 py-2 text-[11px] text-muted-foreground border-t bg-muted/20">
        All figures are in today's purchasing power (CPI-U adjusted). YTD and YoY measure the inflation-adjusted value of the position; Start/End Price are split-adjusted nominal closes. Fee is the fund's reported annual expense ratio — already netted out of the Overall True Return because ETFs deduct it from NAV daily. As of {asOf}.
      </p>
    </div>
  );
}
